// src/pages/MyFormsPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useLocation } from 'react-router-dom';

import { getMyForms } from '../controllers/form.controller';

import DatePicker from 'react-multi-date-picker';
import DateObject from 'react-date-object';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';

const MyFormsPage = () => {
  const [treeData, setTreeData] = useState([]);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // ✅ تاریخ‌های پیشفرض را فقط یکبار بساز (نه هر رندر)
  const { today, fourteenDaysAgo } = useMemo(() => {
    const t = new DateObject({ date: new Date(), calendar: persian, locale: persian_fa });
    const a = new DateObject({ date: new Date(), calendar: persian, locale: persian_fa }).subtract(14, 'days');
    return { today: t, fourteenDaysAgo: a };
  }, []);

  const [startDate, setStartDate] = useState(fourteenDaysAgo); // DateObject
  const [endDate, setEndDate] = useState(today);              // DateObject

  const navigate = useNavigate();
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const subjectId = query.get('subjectId');
  const subjectName = query.get('subjectName');

  const toISOStartOfDay = useCallback((dateObj) => {
    if (!dateObj) return '';
    const d = dateObj.toDate();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, []);

  const toISOEndOfDay = useCallback((dateObj) => {
    if (!dateObj) return '';
    const d = dateObj.toDate();
    d.setHours(23, 59, 59, 999);
    return d.toISOString();
  }, []);

  // ✅ fetchMyForms با useCallback => warning dependency حل
  const fetchMyForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyForms({
        search,
        type,
        page,
        limit,
        startDate: toISOStartOfDay(startDate),
        endDate: toISOEndOfDay(endDate),
      });

      const data = Array.isArray(res) ? res : res?.data || [];

      if (!data.length) {
        // اینجا اگر دوست نداری هر بار toast بیاد، می‌تونی حذفش کنی
        // toast.warn('هیچ داده‌ای برای نمایش وجود ندارد.');
        setTreeData([]);
        setTotalCount(0);
        return;
      }

      const tree = [];
      const projectMap = {};
      const clinicMap = {};
      const pageMap = {};

      data.forEach(item => {
        const projectId = item.projectId;
        const clinicId = item.clinicId;
        const pageId = item.pageId;
        const formId = item.formId;

        // 1) Project
        if (!projectMap[projectId]) {
          const node = {
            key: `project-${projectId}`,
            data: {
              title: item.projectTitle?.fa || 'بدون عنوان',
              projectType: item.projectType || '—',
              type: 'Project'
            },
            children: []
          };
          projectMap[projectId] = node;
          tree.push(node);
        }

        // 2) Clinic
        if (!clinicMap[clinicId]) {
          const node = {
            key: `clinic-${clinicId}`,
            data: {
              title: item.clinicTitle?.fa || 'بدون عنوان',
              type: 'Clinic'
            },
            children: []
          };
          clinicMap[clinicId] = node;
          projectMap[projectId].children.push(node);
        }

        // 3) Page
        if (!pageMap[pageId]) {
          const node = {
            key: `page-${pageId}`,
            data: {
              title: item.pageTitle?.fa || 'بدون عنوان',
              type: 'Page'
            },
            children: []
          };
          pageMap[pageId] = node;
          clinicMap[clinicId].children.push(node);
        }

        // 4) Form
        const formNode = {
          key: `form-${formId}`,
          data: {
            title: item.formTitle?.fa || 'بدون عنوان',
            type: 'Form',
            canEdit: item.canEdit,
            formId: item._id
          },
          leaf: true
        };
        pageMap[pageId].children.push(formNode);
      });

      setTreeData(tree);
      setTotalCount(res.totalCount || 0);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'خطا در دریافت داده‌ها');
    } finally {
      setLoading(false);
    }
  }, [search, type, page, limit, startDate, endDate, toISOStartOfDay, toISOEndOfDay]);

  // ✅ حالا eslint راضی است
  useEffect(() => {
    fetchMyForms();
  }, [fetchMyForms]);

  const actionTemplate = (node) => {
    if (node.data.type === 'Form') {
      return (
        <Button
          label={node.data.canEdit ? 'وضعیت بیماران / پاسخ‌دهی' : 'مشاهده وضعیت'}
          icon={node.data.canEdit ? 'pi pi-users' : 'pi pi-eye'}
          className={`p-button-sm ${node.data.canEdit ? 'p-button-success' : 'p-button-secondary'}`}
          onClick={() => navigate(`/forms/${node.data.formId}/subjects`)}
        />
      );
    }
    return null;
  };

  // ✅ getStatusBadge حذف شد چون استفاده نمی‌شد (warning دوم حل شد)

  return (
    <div className="p-4" dir="rtl">
      <ToastContainer position="top-right" autoClose={3000} />
      <h2 className="p-text-bold p-mb-3">فرم‌های من (Viewer)</h2>

      {subjectId && (
        <div style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
          بیمار انتخاب‌شده:
          <span style={{ marginRight: 8 }}>
            {subjectName || subjectId}
          </span>
        </div>
      )}

      <div
        className="p-card p-shadow-3 p-p-3 p-mb-4"
        style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <span className="p-input-icon-left" style={{ flex: '1 1 200px' }}>
          <i className="pi pi-search" />
          <InputText
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="جستجو در عنوان پروژه"
            style={{ width: '100%' }}
          />
        </span>

        <Dropdown
          value={type}
          onChange={(e) => {
            setType(e.value);
            setPage(1);
          }}
          options={[
            { label: 'همه', value: '' },
            { label: 'آزمایشی', value: 'trial' },
            { label: 'اصلی', value: 'main' }
          ]}
          placeholder="نوع پروژه"
          style={{ flex: '1 1 150px' }}
        />

        <DatePicker
          calendar={persian}
          locale={persian_fa}
          format="YYYY/MM/DD"
          value={startDate || null}
          onChange={(date) => {
            setStartDate(date || null);
            setPage(1);
          }}
          style={{ flex: '1 1 150px', height: 40 }}
          placeholder="از تاریخ"
          calendarPosition="bottom-right"
        />

        <DatePicker
          calendar={persian}
          locale={persian_fa}
          format="YYYY/MM/DD"
          value={endDate || null}
          onChange={(date) => {
            setEndDate(date || null);
            setPage(1);
          }}
          style={{ flex: '1 1 150px', height: 40 }}
          placeholder="تا تاریخ"
          calendarPosition="bottom-right"
        />

        <Button
          label="اعمال فیلتر"
          icon="pi pi-filter"
          className="p-button-primary p-button-raised"
          style={{ flex: '1 1 120px', whiteSpace: 'nowrap' }}
          onClick={() => {
            setPage(1);
            fetchMyForms();
          }}
        />
      </div>

      {loading ? (
        <div className="p-text-center p-mt-5">
          <ProgressSpinner />
        </div>
      ) : (
        <TreeTable value={treeData} className="ltr-table" tableStyle={{ minWidth: '100%' }}>
          <Column field="title" header="عنوان" expander />
          <Column field="type" header="نوع" />
          <Column field="projectType" header="نوع پروژه" />
          <Column body={actionTemplate} header="عملیات" style={{ width: '160px' }} />
        </TreeTable>
      )}

      {totalCount > limit && (
        <div className="p-d-flex p-jc-center p-mt-4 p-ai-center p-gap-3">
          <Button
            label="صفحه قبل"
            icon="pi pi-angle-right"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          />
          <span>صفحه {page} از {Math.ceil(totalCount / limit)}</span>
          <Button
            label="صفحه بعد"
            icon="pi pi-angle-left"
            disabled={page >= Math.ceil(totalCount / limit)}
            onClick={() => setPage(page + 1)}
          />
        </div>
      )}
    </div>
  );
};

export default MyFormsPage;
